// ================================================================================
// Analytics events
//
// Pushes tracking events to the GTM dataLayer. Kept separate from Cornerstone's
// custom-events.js so theme upgrades don't touch it.
//
// Loaded as a module from snippets/theme-globals.liquid ('useAnalyticsEvents'),
// so it's deferred and its top-level declarations stay out of the global scope.
// ================================================================================

const isDesignMode = Boolean(window.Shopify?.designMode);

// ================================================================================
// Helpers
// ================================================================================
const pushEvent = (event, data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...data });
};

// ================================================================================
// Ecommerce item list helpers (view_item_list, select_item)
//
// - Lists are marked in Liquid with [data-analytics-list] plus
//   data-item-list-id / data-item-list-name, and each product inside with
//   [data-analytics-item] (snippets/analytics-item-data.liquid)
// - Only rendered items count, so desktop/mobile duplicates of the same list
//   (routine-steps cards, complementary products) don't double count
// - The index is the item's position among the list's rendered items, so it
//   continues across "Load more" pages
// ================================================================================
const LIST_SELECTOR = "[data-analytics-list]";
const ITEM_SELECTOR = "[data-analytics-item]";

const getListItems = (list) =>
  [...list.querySelectorAll(ITEM_SELECTOR)].filter(
    (el) => el.closest(LIST_SELECTOR) === list && el.getClientRects().length > 0
  );

const buildItem = (list, el, index) => {
  try {
    return {
      ...JSON.parse(el.dataset.analyticsItem),
      index,
      item_list_id: list.dataset.itemListId,
      item_list_name: list.dataset.itemListName,
    };
  } catch {
    return null;
  }
};

const pushEcommerceEvent = (event, list, items) => {
  window.dataLayer = window.dataLayer || [];
  // Clear the previous ecommerce object so its items don't merge into this one
  window.dataLayer.push({ ecommerce: null });
  // Presentment currency, set on window.Shopify by content_for_header
  const currency = window.Shopify?.currency?.active;
  pushEvent(event, {
    ecommerce: {
      ...(currency && { currency }),
      item_list_id: list.dataset.itemListId,
      item_list_name: list.dataset.itemListName,
      items,
    },
  });
};

// ================================================================================
// "Newsletter signup" (footer)
//
// - Pushes a 'newsletter_signup' event when the footer newsletter form
//   (#footer-subscribe) is submitted
// - The native 'submit' event only fires once HTML5 validation passes, so the
//   email is present and well-formed
// ================================================================================
const trackNewsletterSignup = () => {
  document.addEventListener("submit", (evt) => {
    const form = evt.target;
    if (form?.id !== "footer-subscribe") return;

    const emailInput = form.querySelector('input[name="contact[email]"]');

    pushEvent("newsletter_signup", {
      user_email: emailInput?.value.trim() ?? "",
    });
  });
};

// ================================================================================
// "View search results" (quick search)
//
// - Pushes a 'view_search_results' event when predictive search results are
//   rendered in the header quick search dropdown
// - theme.js injects the quick-search-results section into
//   [data-quick-search-results], so we observe the container instead of
//   patching the vendor script
// - Term and count come from the section's [data-quick-search-meta] element, so
//   they always describe the response actually rendered
// - Debounced so only the settled term is tracked (results render per
//   keystroke), and the same term is not pushed twice while the search stays open
// ================================================================================
const trackQuickSearchResults = () => {
  const containers = document.querySelectorAll("[data-quick-search-results]");
  if (!containers.length) return;

  const DEBOUNCE_MS = 1000;
  let timer;
  let lastTerm = "";

  const pushQuickSearch = (container) => {
    const meta = container.querySelector("[data-quick-search-meta]");
    if (!meta) return;

    const searchTerm = (meta.dataset.searchTerm || "").trim();
    if (!searchTerm || searchTerm === lastTerm) return;
    lastTerm = searchTerm;

    pushEvent("quick_search", {
      number_of_results: parseInt(meta.dataset.resultsCount, 10) || 0,
    });
  };

  for (const container of containers) {
    new MutationObserver(() => {
      clearTimeout(timer);
      // theme.js empties the container before each fetch; wait for results
      if (!container.childElementCount) return;
      timer = setTimeout(() => pushQuickSearch(container), DEBOUNCE_MS);
    }).observe(container, { childList: true });
  }

  // Reset when the search closes, so searching the same term again later
  // counts as a new view. Also drops a pending push the user never settled on.
  for (const quickSearch of document.querySelectorAll("[data-quick-search]")) {
    new MutationObserver(() => {
      if (quickSearch.dataset.quickSearchActive !== "false") return;
      clearTimeout(timer);
      lastTerm = "";
    }).observe(quickSearch, { attributes: true, attributeFilter: ["data-quick-search-active"] });
  }
};

// ================================================================================
// "Sort / filter click" (collection & search)
//
// - Pushes a 'sort_filter_click' event when a filter option is selected or a
//   sort option is chosen
// - The option name comes from the input's data-track-label, set in Liquid as
//   'filter_<group>_<option>' (filter-item.liquid) or 'sort_<option>'
//   (filter-form.liquid / filter-topbar.liquid)
// - Delegated, since theme.js re-renders the filter inputs after each update
// - Only selections are tracked; unchecking a filter doesn't push
// ================================================================================
const trackSortFilterClick = () => {
  document.addEventListener("change", (evt) => {
    const input = evt.target;
    if (!input?.matches?.("input[data-track-label]")) return;
    if (!input.checked) return;

    pushEvent("sort_filter_click", {
      options: input.dataset.trackLabel,
    });
  });
};

// ================================================================================
// "Sort / filter click" - price range (collection & search)
//
// - Pushes a 'sort_filter_click' event with 'filter_price_<low>_to_<high>'
//   when the price range changes (slider handle released or input edited)
// - theme.js dispatches a 'change' per handle, so moving both in one gesture
//   fires twice. Debounced to push once with the final range
// - Values are read at change time, since theme.js re-renders the inputs after
//   each update. Empty min falls back to 0, empty max to the range max
//   (the input's placeholder)
// ================================================================================
const trackPriceRangeFilter = () => {
  const DEBOUNCE_MS = 500;
  let timeout = null;
  let range = null;

  document.addEventListener("change", (evt) => {
    const input = evt.target;
    if (!input?.matches?.("input[data-range-input]")) return;

    const wrapper = input.closest("[data-price-range]");
    const minInput = wrapper?.querySelector('[data-range-input-type="min"]');
    const maxInput = wrapper?.querySelector('[data-range-input-type="max"]');
    if (!minInput || !maxInput) return;

    range = {
      low: Math.floor(minInput.value || 0),
      high: Math.floor(maxInput.value || maxInput.placeholder),
    };

    clearTimeout(timeout);
    timeout = setTimeout(() => {
      pushEvent("sort_filter_click", {
        options: `filter_price_${range.low}_to_${range.high}`,
      });
    }, DEBOUNCE_MS);
  });
};

// ================================================================================
// "Share interaction" (social share)
//
// - Pushes a 'share_interaction' event from the social-share snippet
//   (product and article pages) and the share-icons snippet
// - 'clicked': the share button is clicked to open the share popup or the
//   native OS share sheet. share_to is empty, since no platform is chosen yet
// - 'shared': a share target is chosen. share_to comes from the target's
//   data-share-to ('twitter', 'facebook', 'pinterest', 'copy_link')
// - Native OS share: theme.js calls navigator.share() directly, so we wrap it
//   to push 'shared' once the share completes (cancelling doesn't push). The
//   OS doesn't expose which app was picked, so share_to is 'native_share'
// ================================================================================
const trackShareInteraction = () => {
  const pushShare = (interaction, shareTo) => {
    pushEvent("share_interaction", {
      share_interaction: interaction,
      share_to: shareTo,
    });
  };

  document.addEventListener("click", (evt) => {
    if (!evt.target.closest) return;

    const shareButton = evt.target.closest(".social-share__button");
    // theme.js toggles the popup from a window listener, which runs after this
    // one, so aria-expanded is still the pre-click state. Only track opening.
    if (shareButton && shareButton.getAttribute("aria-expanded") !== "true") {
      pushShare("clicked", "");
      return;
    }

    const shareTarget = evt.target.closest("[data-share-to]");
    if (shareTarget) {
      pushShare("shared", shareTarget.dataset.shareTo);
    }
  });

  if (typeof navigator.share === "function") {
    const nativeShare = navigator.share.bind(navigator);
    navigator.share = async (data) => {
      const result = await nativeShare(data);
      pushShare("shared", "native_share");
      return result;
    };
  }
};

// ================================================================================
// "View item list"
//
// - Pushes a GA4 'view_item_list' event when a product list scrolls into view
// - Each item element is reported once per page view. A list fires again only
//   when it gains items that weren't reported yet:
//   - tabs (tabbed-collections, routine-steps): each tab fires the first time
//     it is shown, since hidden panels have no visible items
//   - PLP: filter/sort re-renders the grid (new elements), and "Load more"
//     appends items, so both push the newly presented products
//   - recommendations / complementary products are fetched after load, so
//     they push once their items are injected
// ================================================================================
const trackViewItemList = () => {
  if (isDesignMode) return;

  const CHECK_DELAY_MS = 150;

  const visibleLists = new Set();
  const observedLists = new WeakSet();
  const reportedItems = new WeakSet();
  let checkTimer;

  const reportList = (list) => {
    const items = getListItems(list)
      .map((el, index) => {
        if (reportedItems.has(el)) return null;
        reportedItems.add(el);
        return buildItem(list, el, index);
      })
      .filter(Boolean);

    if (items.length) pushEcommerceEvent("view_item_list", list, items);
  };

  // Debounced, so DOM updates (tab templates, fetched recommendations, Load
  // more) settle and become visible before items are collected
  const scheduleCheck = () => {
    clearTimeout(checkTimer);
    checkTimer = setTimeout(() => {
      for (const list of visibleLists) {
        if (list.isConnected) {
          reportList(list);
        } else {
          visibleLists.delete(list);
        }
      }
    }, CHECK_DELAY_MS);
  };

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (isIntersecting) {
          visibleLists.add(target);
        } else {
          visibleLists.delete(target);
        }
      }
      scheduleCheck();
    },
    // The list must be at least 100px into the viewport to count as seen
    { rootMargin: "0px 0px -100px 0px" }
  );

  const observe = (list) => {
    if (observedLists.has(list)) return;
    observedLists.add(list);
    intersectionObserver.observe(list);
  };

  const observeLists = (root) => {
    if (root.matches?.(LIST_SELECTOR)) observe(root);
    root.querySelectorAll(LIST_SELECTOR).forEach(observe);
  };

  observeLists(document);

  new MutationObserver((mutations) => {
    let hasNewItems = false;

    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        observeLists(node);
        if (node.matches(ITEM_SELECTOR) || node.querySelector(ITEM_SELECTOR)) hasNewItems = true;
      }
    }

    if (hasNewItems) scheduleCheck();
  }).observe(document.body, { childList: true, subtree: true });
};

// ================================================================================
// "Select item"
//
// - Pushes a GA4 'select_item' event when a link to a PDP is clicked inside an
//   item of a tracked list, or wrapping one (the same lists as view_item_list)
// - Any PDP link in the item counts: image, title, swatches, "Shop now" buttons
// - Middle clicks (open in new tab) are tracked through 'auxclick'
// - Delegated, since lists are re-rendered and injected after load
// ================================================================================
const trackSelectItem = () => {
  if (isDesignMode) return;

  const onClick = (evt) => {
    if (evt.type === "auxclick" && evt.button !== 1) return;

    const link = evt.target.closest?.("a[href]");
    if (!link?.pathname.includes("/products/")) return;

    // The link is either inside the item or wraps it (routine-steps cards)
    const itemEl = link.closest(ITEM_SELECTOR) ?? link.querySelector(ITEM_SELECTOR);
    const list = itemEl?.closest(LIST_SELECTOR);
    if (!list) return;

    const item = buildItem(list, itemEl, getListItems(list).indexOf(itemEl));
    if (item) pushEcommerceEvent("select_item", list, [item]);
  };

  document.addEventListener("click", onClick);
  document.addEventListener("auxclick", onClick);
};

// ================================================================================
// "Choose options click" (product cards)
//
// - Pushes a 'choose_options_click' event when the quick add button of a
//   product with variants is clicked ("Choose options"), which opens the quick
//   product modal instead of adding to cart
// - Single-variant products add to cart directly, so they don't push
// - Listens in the capture phase, since theme.js stops propagation on the
//   button click
// ================================================================================
const trackChooseOptionsClick = () => {
  if (isDesignMode) return;

  document.addEventListener(
    "click",
    (evt) => {
      const button = evt.target.closest?.(".product-item__quick-add");
      if (!button?.closest('.product-item__quick-add-wrapper[data-has-variants="true"]')) return;

      pushEvent("choose_options_click", {
        choose_options_option: button.closest(".product-item")?.dataset.title || "",
      });
    },
    true
  );
};

// ================================================================================
// "View item" (PDP)
//
// - Pushes a GA4 'view_item' event once when a PDP loads, for the variant
//   selected on load
// - The payload is rendered by the main product section
//   (snippets/analytics-view-item.liquid), so featured product sections and
//   quick view don't count as a PDP view
// ================================================================================
const trackViewItem = () => {
  if (isDesignMode) return;

  const dataEl = document.querySelector("[data-analytics-view-item]");
  if (!dataEl) return;

  let ecommerce;
  try {
    ecommerce = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  pushEvent("view_item", { ecommerce });
};

// ================================================================================
// "View promotion" / "Select promotion" (announcement bar, slideshow)
//
// - Only announcements and slides with "Track as promotion" checked are
//   marked with [data-analytics-promotion]
//   (snippets/analytics-promotion-data.liquid)
// - 'view_promotion' is pushed once per promotion per page view, the first time
//   at least half of it is visible. Both are scroll sliders that clip their
//   slides, so each slide counts when it scrolls into view
// - 'select_promotion' is pushed when a link inside a promotion is clicked
//   (slide image, buttons, announcement link; middle clicks through
//   'auxclick'). The announcement "details" modal button isn't a link, so it
//   doesn't count
// ================================================================================
const PROMOTION_SELECTOR = "[data-analytics-promotion]";

const readPromotion = (el) => {
  try {
    return JSON.parse(el.dataset.analyticsPromotion);
  } catch {
    return null;
  }
};

const pushPromotionEvent = (event, promotion) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  pushEvent(event, { ecommerce: promotion });
};

const trackViewPromotion = () => {
  if (isDesignMode) return;

  const promotions = document.querySelectorAll(PROMOTION_SELECTOR);
  if (!promotions.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);

        const promotion = readPromotion(entry.target);
        if (promotion) pushPromotionEvent("view_promotion", promotion);
      }
    },
    { threshold: 0.5 }
  );

  promotions.forEach((el) => observer.observe(el));
};

const trackSelectPromotion = () => {
  if (isDesignMode) return;

  const onClick = (evt) => {
    if (evt.type === "auxclick" && evt.button !== 1) return;

    const promotionEl = evt.target.closest?.("a[href]")?.closest(PROMOTION_SELECTOR);
    if (!promotionEl) return;

    const promotion = readPromotion(promotionEl);
    if (promotion) pushPromotionEvent("select_promotion", promotion);
  };

  document.addEventListener("click", onClick);
  document.addEventListener("auxclick", onClick);
};

// ================================================================================
// Cart helpers (add_to_cart, remove_from_cart, view_cart)
//
// - Items are built from /cart.js line items, or the same fields rendered by
//   snippets/analytics-cart-data.liquid
// - The native fetch is kept before trackCartChanges wraps it, so reading the
//   cart never counts as a cart change
// ================================================================================
const cartJsUrl = `${window.theme?.routes?.cart?.base || "/cart"}.js`;
const nativeFetch = window.fetch.bind(window);

const fetchCart = async () => {
  const response = await nativeFetch(cartJsUrl, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
};

const readRenderedCart = () => {
  const dataEl = document.querySelector("[data-analytics-cart]");
  if (!dataEl) return null;

  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    return null;
  }
};

const toCartItem = (line, quantity) => {
  const price = line.final_price / 100;
  const discount = (line.original_price - line.final_price) / 100;

  return {
    ...(line.sku ? { item_id: line.sku } : {}),
    item_name: line.product_title,
    item_brand: line.vendor,
    item_category: line.product_type,
    ...(line.product_has_only_default_variant ? {} : { item_variant: line.variant_title }),
    price,
    ...(discount > 0 ? { discount } : {}),
    quantity,
  };
};

const pushCartEvent = (event, currency, items) => {
  if (!items.length) return;

  const value = items.reduce((total, item) => total + item.price * item.quantity, 0);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  pushEvent(event, {
    ecommerce: {
      currency,
      value: Math.round(value * 100) / 100,
      items,
    },
  });
};

// ================================================================================
// "Add to cart" / "Remove from cart"
//
// - Pushes GA4 'add_to_cart' when cart quantities go up and 'remove_from_cart'
//   when they go down, from any source: PDP form, quick add, quick cart and
//   cart page quantity buttons/inputs, remove buttons, and apps
// - theme.js (and apps) change the cart through fetch, so we wrap fetch and,
//   after every successful /cart/add|change|update|clear request, compare
//   /cart.js with the previous cart line by line (by line item key). Comparing
//   carts instead of reading request bodies covers every request format, and
//   counts what Shopify actually applied (e.g. capped by inventory)
// - The starting cart is rendered with the page (snippets/analytics-cart-data
//   .liquid). It's re-read when the tab becomes visible again or the page is
//   restored from the back/forward cache, so changes made in another tab
//   aren't reported twice
// - Requests are processed in order, so rapid +/- clicks are each counted once
// - One event per direction per cart change; items carry the quantity that
//   changed, and value is the total of those items
// ================================================================================
const trackCartChanges = () => {
  if (isDesignMode) return;

  let snapshot = readRenderedCart();
  if (!snapshot) return;

  const CART_MUTATION_PATH = /\/cart\/(add|change|update|clear)(\.js)?$/;

  let queue = Promise.resolve();
  let pendingMutations = 0;

  const isCartMutation = (input, init) => {
    const request = input instanceof Request ? input : null;
    const method = (init?.method || request?.method || "GET").toUpperCase();
    if (method !== "POST") return false;

    try {
      const url = new URL(request ? request.url : input, window.location.href);
      return CART_MUTATION_PATH.test(url.pathname);
    } catch {
      return false;
    }
  };

  const reportChanges = (previousCart, cart) => {
    const previousLines = new Map(previousCart.items.map((line) => [line.key, line]));
    const added = [];
    const removed = [];

    for (const line of cart.items) {
      const delta = line.quantity - (previousLines.get(line.key)?.quantity ?? 0);
      if (delta > 0) added.push(toCartItem(line, delta));
      if (delta < 0) removed.push(toCartItem(line, -delta));
      previousLines.delete(line.key);
    }

    // Lines no longer in the cart were removed entirely
    for (const line of previousLines.values()) {
      removed.push(toCartItem(line, line.quantity));
    }

    pushCartEvent("add_to_cart", cart.currency, added);
    pushCartEvent("remove_from_cart", cart.currency, removed);
  };

  const enqueue = (task) => {
    queue = queue.then(task).catch(() => {});
    return queue;
  };

  const syncCart = ({ report }) =>
    enqueue(async () => {
      const cart = await fetchCart();
      if (report) reportChanges(snapshot, cart);
      snapshot = cart;
    });

  window.fetch = async (input, init) => {
    if (!isCartMutation(input, init)) return nativeFetch(input, init);

    pendingMutations += 1;
    try {
      const response = await nativeFetch(input, init);
      if (response.ok) syncCart({ report: true });
      return response;
    } finally {
      pendingMutations -= 1;
    }
  };

  // Re-read the cart without reporting when it may have changed elsewhere.
  // Skipped while a request is in flight, since that change must be reported.
  const refreshSnapshot = () => {
    if (pendingMutations === 0) syncCart({ report: false });
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refreshSnapshot();
  });

  window.addEventListener("pageshow", (evt) => {
    if (evt.persisted) refreshSnapshot();
  });
};

// ================================================================================
// "View cart"
//
// - Pushes a GA4 'view_cart' event when the cart page loads and every time the
//   quick cart (popup cart) opens, with every line at its full quantity
// - Cart page: uses the cart rendered with the page (analytics-cart-data)
// - Quick cart: theme.js only emits 'quick-cart:open' on its internal event
//   bus (no DOM event), so we watch .quick-cart__wrapper gaining the 'active'
//   class instead, then read /cart.js so items added just before opening are
//   included
// - Empty carts don't push, since there are no items to report
// ================================================================================
const trackViewCart = () => {
  if (isDesignMode) return;

  const pushViewCart = (cart) =>
    pushCartEvent(
      "view_cart",
      cart.currency,
      cart.items.map((line) => toCartItem(line, line.quantity))
    );

  if (document.querySelector('[data-section-type="cart"]')) {
    const cart = readRenderedCart();
    if (cart) pushViewCart(cart);
  }

  const wrapper = document.querySelector(".quick-cart__wrapper");
  if (!wrapper) return;

  let isOpen = wrapper.classList.contains("active");

  new MutationObserver(() => {
    const wasOpen = isOpen;
    isOpen = wrapper.classList.contains("active");
    if (isOpen && !wasOpen) fetchCart().then(pushViewCart).catch(() => {});
  }).observe(wrapper, { attributes: true, attributeFilter: ["class"] });
};

trackNewsletterSignup();
trackQuickSearchResults();
trackSortFilterClick();
trackPriceRangeFilter();
trackShareInteraction();
trackViewItemList();
trackSelectItem();
trackChooseOptionsClick();
trackViewItem();
trackViewPromotion();
trackSelectPromotion();
trackCartChanges();
trackViewCart();
